import PipelineNodeDataTable from "@components/pipelineNodes/PIpelineNodeDataTable";
import { NodeList, SingleNodeItem } from "@components/pipelineNodes/PipelineNodeList.component";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import Warning from "@components/statusIndicators/Warning.component";
import { PipelineNode, PipelineNodeORM } from "@models/pipelineNode";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent, WizardContent, WizardFooter, WizardStep, WizardStepDivider, WizardSteps } from "@pages/PageStructure.component";
import DataSourceImporter from "@pages/Sources/DataSourceImporter.component";
import { SnowflakeDataSourceViewer, SnowflakeTableSelector } from "@pages/Sources/SnowflakeDataSource.page";
import { getErrorMessage } from "@services/errors.service";
import { summarizeNumber } from "@services/formatting.service";
import { getGroupValueForNodeType } from "@services/modeling.service";
import toast from "@services/toast.service";
import { useQueryParams } from "@services/url.service";
import { invalidatePipelineNode, savePipelineNode, useFile, usePipelineNode, usePipelineNodes, useSourceRecordType } from "@stores/data.store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BigSelectorComponent, Grid } from "./shared";
import SuccessAlert from "@components/statusIndicators/SuccessAlert.component";
import { shortid } from "@services/id.service";
import { BuildOrchestration } from "@models/buildOrchestration";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import PipelineNodeName from "@components/pipelineNodes/PipelineNodeName.component";
import { BlockInProd } from "@components/project/DraftModeRequired.component";
import { sampleData  } from "../../constants/samples";
import { runBuild } from "@services/alert/alert.service";
import TemplateGroupORM from "@models/templateGroup";
import { tab } from "@testing-library/user-event/dist/tab";
import LoadingCard from "@components/card/LoadingCard.component";




const MultiDataSourceWizardPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [sourceType, setSourceType] = useState('snowflake');

    const queryParams = useQueryParams();
    const [step, setStep] = useState(0);

    const [creatingEntities, setCreatingEntities] = useState(false);


    const next = queryParams.get('next');
    const boContext = queryParams.get('bo_context');
    const querySourceType = queryParams.get('sourceType');

    const [processedQuerySourceType, setProcessedQuerySourceType] = useState(false);    


    const [loadFromUrl, setLoadFromUrl] = useState('');
    const urlQueryParam = searchParams.get('url') || '';

    useEffect(() => {
        if(!!urlQueryParam) {
            setLoadFromUrl(urlQueryParam);
            setSourceType('file')
            setStep(1);
        }
    }, [urlQueryParam]);

    useEffect(() => {
        if (querySourceType && !processedQuerySourceType) {
            setSourceType(querySourceType);
            setProcessedQuerySourceType(true);
            setStep(1);
        }
    }, [querySourceType, processedQuerySourceType]);

    const boNode = usePipelineNode(boContext || undefined, !!boContext);

    const allNodes = usePipelineNodes();

    const navigate = useNavigate();

    const [selectedOtherNode, setSelectedOtherNode] = useState<PipelineNode|undefined>(undefined);



    const [snowflakeTableNames, setSnowflakeTableNames] = useState<string[]>([]);

    
    const nextIsEnabled = useMemo(() => {
        if (step == 0) {
            
            if (sourceType == 'snowflake' && snowflakeTableNames.length > 0 && !creatingEntities) {
                return true;
            }
            return false;
        }
        
    }, [sourceType, snowflakeTableNames, selectedOtherNode, step, creatingEntities]);

    const [newNodeName, setNewNodeName] = useState('');
    const [newNodeDescription, setNewNodeDescription] = useState('');


    const isSaveable = useMemo(() => {
        if (sourceType == 'snowflake') {
            return !!snowflakeTableNames && !!newNodeName;
        } else if (sourceType == 'other') {
            return !!selectedOtherNode;
        }
        return false;
    }, [newNodeName, sourceType, selectedOtherNode, snowflakeTableNames]);

    const [building, setBuilding] = useState(false);

    const nextMessage = useMemo(() => {
        if (step == 0) {
            if (snowflakeTableNames.length == 0) {
                return 'No tables selected';
            } else  {
                return `Select ${snowflakeTableNames.length} table${snowflakeTableNames.length > 1 ? 's' : ''}`;
            }
        } else {
            return 'Next';
        }
    }, [step, snowflakeTableNames.length]);

    const goNext = useCallback(() => {
        if (step == 0 && snowflakeTableNames.length > 0) {
            onCreatedSourceAndEntity();
        } else {
            setStep(step + 1);
        }
    }, [step, sourceType, snowflakeTableNames]);


    const onCreatedSourceAndEntity = useCallback(async () => {
        setCreatingEntities(true);
        try {
            const resp = await TemplateGroupORM.batchAutoEntities({
                from_tables: snowflakeTableNames.map(t => ({table_name: t}))
            });

            await runBuild(
                '',
                false,
                false,
                <>
                    <Link to={`/model/list`} className="btn btn-success">View Models</Link>
                </>,
                <>
                    <Link className="btn btn-light me-1" to={`/`}>Fix Errors</Link>
                </>
            );
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setCreatingEntities(false);
        }

    }, [snowflakeTableNames]);

    return <PageStructure>
        <PageContent>
            <Pane>
                <PaneContent>
                    <PageContentHeader>
                        <div className="d-flex center-vertically" style={{height: '100%'}}>
                            <DashboardIcon icon="mdi mdi-wizard-hat" bgColor="pliable"/>

                            <div className="">
                                <h1 className="mb-0">Auto Entity Wizard</h1>
                                <div className="text-muted font-13">
                                    Load data into Pliable.
                                </div>
                            </div>
                            <div className="flex-1" style={{maxWidth: 'calc(60vw)'}}>
                                <WizardSteps>
                                    <WizardStep
                                        stepNumber="1"
                                        icon="mdi mdi-view-list"
                                        stepDescription="Select Tables"
                                        active={step == 0}
                                        complete={step >= 0}
                                    />
                                    <WizardStepDivider />
                                    <WizardStep
                                        stepNumber="2"
                                        icon="mdi mdi-information"
                                        stepDescription="Create Entities"
                                        active={step == 1}
                                    />
                                    <WizardStepDivider />
                                    <WizardStep
                                        stepNumber="2"
                                        icon="mdi mdi-information"
                                        stepDescription="Review"
                                        active={step == 2}
                                    />

                                </WizardSteps>
                            </div>
                            
                            {boNode.data && <>
                                <div>
                                <div className="small">Connecting source data to:</div>
                                    <SingleNodeItem node={boNode.data} compact rounded/>
                                </div>
                            </>}
                            
                        </div>
                    </PageContentHeader>
                    <PageContentInner hasHeader noScroll>
                    <BlockInProd>
                        <Pane>
                            
                            <WizardContent className="mt-3">
                                <div className="card">
                                    <div className="card-body overflow-hidden scroll-vertical">
                                        {step == 2 && <>
                                            <div className="row">
                                                <div className="col-3"></div>
                                                <div className="col-6">
                                                    <h2>Review</h2>
                                                    
                                                    {['snowflake', 'file'].includes(sourceType) && <>
                                                        <hr />
                                                        
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Name this source</Form.Label>
                                                            <input type="text" className="form-control" value={newNodeName} onChange={e => setNewNodeName(e.target.value)} />
                                                            <Form.Text>You can name it however you'd like, including spaces and special characters. We'll automatically generate a DBT-safe model name.</Form.Text>
                                                            
                                                        </Form.Group>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Describe this source</Form.Label>
                                                            <Form.Control as="textarea" className="form-control" value={newNodeDescription} onChange={e => setNewNodeDescription(e.target.value)} />
                                                            <Form.Text>Give it a good description so your colleages can find data in Pliable.</Form.Text>
                                                            
                                                        </Form.Group>
                                                    </>}
                                                    {sourceType == 'other' && selectedOtherNode && <div className="mb-3">
                                                        <p>Using data from existing node:</p>
                                                        <SingleNodeItem node={selectedOtherNode} rounded/>
                                                    </div>}

                                                    <button onClick={onCreatedSourceAndEntity} disabled={!isSaveable} className="btn btn-large btn-outline">Create Source + Entity</button>
                                                </div>
                                                <div className="col-3">
                                                    
                                                </div>
                                            </div>
                                            
                                            
                                        </>}
                                        {step == 0 && <>
                                            {sourceType == 'snowflake' && !creatingEntities && <>
                                                <div className="row" style={{height: '100%'}}>
                                                    <div className="col-3 border-righ overflow-auto" style={{height: '100%'}}>
                                                        <h2>Select tables:</h2>
                                                        <SnowflakeTableSelector
                                                            onSelect={setSnowflakeTableNames}
                                                            selectedTables={snowflakeTableNames}
                                                            multiMode={true}
                                                        />
                                                    </div>
                                                    <div className="col-9 overflow-auto" style={{height: '100%'}}>
                                                        <SnowflakeDataSourceViewer
                                                            tableName={snowflakeTableNames[snowflakeTableNames.length - 1]}
                                                            onUseTable={() => {
                                                                // onUseSnowflakeTable();
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                
                                            </>}

                                            {sourceType == 'snowflake' && creatingEntities && <>
                                                <div className="row" style={{height: '100%'}}>
                                                    <LoadingCard action="Creating Models" />
                                                </div>
                                                
                                            </>}

                                            {sourceType == 'other' && <>
                                                <div className="row" style={{height: '100%'}}>
                                                    <div className="col-3 border-right" style={{height: '100%'}}>
                                                        <h2>Select an existing node.</h2>
                                                        <Form.Group>
                                                            <PipelineNodeSelector
                                                                onSelect={setSelectedOtherNode}
                                                                selectedId={selectedOtherNode?.id || ''}
                                                                optionFilter={(node) => ['SOURCE', 'STAGING'].includes(getGroupValueForNodeType(node.node_type))}
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-9" style={{height: '100%'}}>
                                                        {!selectedOtherNode && <>
                                                            Select a node to view its data.
                                                        </>}
                                                        {selectedOtherNode && <>
                                                            <div style={{height: '80px'}} className="border-bottom">
                                                                <div className="d-flex center-vertically mb-2">
                                                                    
                                                                    <div className="overflow-ellipsis flex-1">
                                                                        <>
                                                                            <h1 className="overflow-ellipsis mb-0">{selectedOtherNode?.label}</h1>
                                                                            <div className="mb-1 font-poppins text-muted font-13">
                                                                                <span className="me-1">
                                                                                    <Badge>{selectedOtherNode?.node_type}</Badge>
                                                                                </span>
                                                                                <span>
                                                                                    {selectedOtherNode?.last_build_completed == null && <Badge bg="warning">Never Built</Badge>}
                                                                                </span>
                                                                                <span className="me-3">&nbsp;</span>
                                                                                <span>
                                                                                    <i className="mdi mdi-file-multiple"></i> Records: {summarizeNumber(selectedOtherNode?.total_records || 0)}
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={{height: 'calc(100% - 80px)'}}>
                                                                
                                                                <PipelineNodeDataTable
                                                                    justTable
                                                                    pipelineNodeId={selectedOtherNode?.id || ''}
                                                                    searchQuery=""
                                                                />
                                                                
                                                                
                                                            </div>
                                                        </>}
                                                    </div>
                                                
                                                </div>
                                            </>}
                                        </>}
                                    </div>
                                </div>
                            </WizardContent>
                            <WizardFooter>
                                <div className="row">
                                    <div className="col-6">
                                        {step > 0 && <button onClick={() => {
                                            setStep(step - 1);
                                        }} className="btn btn-outline-secondary">&larr; Back</button>}
                                    </div>
                                    <div className="col-6 text-end">
                                        {step < 2 && <button onClick={goNext} disabled={!nextIsEnabled} className="btn btn-dark">{nextMessage} &rarr;</button>}
                                    </div>
                                </div>
                            </WizardFooter>
                        </Pane>
                    </BlockInProd>
                    </PageContentInner>
                    
                                                    
                       
                       
                        
                        
                </PaneContent>
            </Pane>
        </PageContent>
        
    </PageStructure>
}

export default MultiDataSourceWizardPage;